class CartDrawer {
    constructor() {
        this.drawer = document.getElementById('CartDrawer');
        if (!this.drawer) return;

        this.itemsContainer = this.drawer.querySelector('[data-cart-items]');
        this.subtotalElement = this.drawer.querySelector('[data-cart-subtotal]');
        this.countElement = this.drawer.querySelector('[data-cart-count]');
        this.checkoutBtn = this.drawer.querySelector('[data-checkout-btn]');
        this.noteElement = this.drawer.querySelector('[data-cart-note]');
        this.deliveryTrust = this.drawer.querySelector('[data-cart-delivery-trust]');
        this.acceleratedCheckout = this.drawer.querySelector('[data-cart-accelerated]');
        this.reviewHoldElement = this.drawer.querySelector('[data-cart-review-hold]');

        this.emptyText = this.drawer.dataset.emptyText || 'Your cart is empty';
        this.emptySubtext = this.drawer.dataset.emptySubtext || "Looks like you haven't added anything yet.";
        this.moneyFormat = this.drawer.dataset.moneyFormat || '${{amount}}';
        this.routes = window.themeRoutes || {};
        this.hasModalController = !!window.SAModalController;
        this._refreshQueued = false;
        this.modalConfig = {
            openClass: 'is-open',
            closeSelector: '[data-cart-close]',
            closeOnEscape: true,
            trapFocus: true
        };

        this.bindEvents();
        this.fetchCart();
    }

    escapeHtml(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    bindEvents() {
        // Open Triggers (delegated so dynamically injected triggers also work)
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-cart-trigger]');
            if (!trigger) return;
            e.preventDefault();
            this.open();
        });

        // Close Triggers (delegated to support dynamically injected close buttons)
        if (this.hasModalController) {
            window.SAModalController.bind(this.drawer, this.modalConfig);
        } else {
            this.drawer.addEventListener('click', (e) => {
                if (e.target.closest('[data-cart-close]')) {
                    this.close();
                }
            });
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
                    this.close();
                }
            });
        }

        // Quantity & Remove Delegation
        if (this.itemsContainer) {
            this.itemsContainer.addEventListener('click', (e) => {
                if (e.target.closest('[data-qty-minus]')) {
                    const key = e.target.closest('[data-qty-minus]').dataset.key;
                    this.updateQuantity(key, -1);
                }
                if (e.target.closest('[data-qty-plus]')) {
                    const key = e.target.closest('[data-qty-plus]').dataset.key;
                    this.updateQuantity(key, 1);
                }
                if (e.target.closest('[data-remove-item]')) {
                    const key = e.target.closest('[data-remove-item]').dataset.key;
                    this.removeItem(key);
                }
            });
        }

        // Global cart refresh events from product cards/forms/tools
        const refresh = () => this.queueFetchCart();
        document.addEventListener('cart:refresh', refresh);
        window.addEventListener('cart:updated', refresh);
        window.addEventListener('cart:refresh', refresh);
    }

    queueFetchCart() {
        if (this._refreshQueued) return;
        this._refreshQueued = true;
        requestAnimationFrame(() => {
            this._refreshQueued = false;
            this.fetchCart();
        });
    }

    async requestCart(url, options) {
        const res = await fetch(url, options);
        if (!res.ok) {
            throw new Error(`Cart request failed (${res.status})`);
        }
        return res.json();
    }

    open() {
        if (!this.drawer) return;
        if (this.hasModalController) {
            window.SAModalController.open(this.drawer, this.modalConfig);
        } else {
            this.drawer.classList.add('is-open');
            this.drawer.setAttribute('aria-hidden', 'false');
            // document.body.style.overflow = 'hidden';
        }
        this.queueFetchCart();
    }

    close() {
        if (!this.drawer) return;
        if (this.hasModalController) {
            window.SAModalController.close(this.drawer, this.modalConfig);
        } else {
            this.drawer.classList.remove('is-open');
            this.drawer.setAttribute('aria-hidden', 'true');
            // document.body.style.overflow = '';
        }
    }

    async fetchCart() {
        try {
            const cart = await this.requestCart((this.routes.cart || '/cart') + '.js?t=' + Date.now(), { cache: 'no-store' });
            this.render(cart);
            return cart;
        } catch (err) {
            console.error('Error fetching cart:', err);
        }
    }

    async updateQuantity(key, change) {
        // Find current qty
        if (!this.itemsContainer) return;
        const input = this.itemsContainer.querySelector(`input[data-key="${key}"]`);
        if (!input) return;
        const currentQty = parseInt(input.value, 10);
        if (Number.isNaN(currentQty)) return;
        const newQty = currentQty + change;

        if (newQty < 0) return;

        try {
            const cart = await this.requestCart((this.routes.cartChange || '/cart/change') + '.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: newQty })
            });
            this.render(cart);
            this.updateHeaderCount(cart.item_count);
        } catch (err) {
            console.error('Error updating cart:', err);
        }
    }

    async removeItem(key) {
        try {
            const cart = await this.requestCart((this.routes.cartChange || '/cart/change') + '.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 })
            });
            this.render(cart);
            this.updateHeaderCount(cart.item_count);
        } catch (err) {
            console.error('Error removing item:', err);
        }
    }

    async addItem(id, quantity = 1, properties = {}) {
        if (!this.drawer) return;
        const existingDigital = this.cart && this.cart.items
            ? this.cart.items.find((item) => String(item.variant_id) === String(id) && item.requires_shipping === false)
            : null;
        if (existingDigital) {
            this.open();
            return this.cart;
        }
        try {
            const added = await this.requestCart((this.routes.cartAdd || '/cart/add') + '.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: id, quantity: quantity, properties: properties })
            });
            this.open();
            await this.fetchCart();
            return added;
        } catch (err) {
            console.error('Error adding item:', err);
            alert('Couldn\'t add that item. Please try again.');
        }
    }

    render(cart) {
        this.cart = cart;
        // Update Counts
        if (this.countElement) this.countElement.textContent = cart.item_count;
        this.updateHeaderCount(cart.item_count);
        this.updateHeaderPreview(cart);

        // Update Subtotal
        if (this.subtotalElement) this.subtotalElement.textContent = this.formatMoney(cart.total_price);
        const allDigital = cart.item_count > 0 && cart.items.every((item) => item.requires_shipping === false);
        const hasPreorder = cart.items.some((item) => {
            const fulfillment = item.properties && item.properties._Fulfillment;
            return fulfillment === 'preorder' || fulfillment === 'partial';
        });
        const hasReviewHold = cart.items.some((item) => item.handle === 'flux-spark-bundle');
        if (this.noteElement) {
            this.noteElement.textContent = hasReviewHold
                ? 'Ordering is paused for one item in this cart.'
                : hasPreorder
                ? 'This cart contains a preorder. Check each product page for timing.'
                : (allDigital ? 'Digital items — no shipping.' : 'Shipping and taxes calculated at checkout.');
        }
        if (this.deliveryTrust) {
            this.deliveryTrust.textContent = hasReviewHold
                ? 'Checkout paused'
                : hasPreorder
                ? 'Contains a preorder'
                : (allDigital ? 'Digital items — no shipping' : 'Checkout on Shopify');
        }
        if (this.reviewHoldElement) this.reviewHoldElement.hidden = !hasReviewHold;
        if (this.acceleratedCheckout) this.acceleratedCheckout.hidden = cart.item_count === 0 || hasReviewHold;

        // Render Items
        if (cart.item_count === 0) {
            if (this.itemsContainer) {
                this.itemsContainer.innerHTML = `
        <div class="cart-empty-state animate-fade-up">
           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" class="empty-icon" stroke="currentColor" stroke-width="1" style="opacity: 0.2; margin-bottom: 24px;">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
           </svg>
          <p class="heading-s" style="margin-bottom: 8px;">${this.emptyText}</p>
          <p class="body-xs text-muted" style="margin-bottom: 24px;">${this.emptySubtext}</p>
          <a class="sa-btn sa-btn-primary animate-shine" href="${this.routes.allProducts || '/collections/all'}">Browse the shop</a>
        </div>
      `;
            }
            if (this.checkoutBtn) this.checkoutBtn.disabled = true;
        } else {
            if (this.checkoutBtn) this.checkoutBtn.disabled = hasReviewHold;
            if (this.itemsContainer) {
                this.itemsContainer.innerHTML = cart.items.map((item, index) => {
                    const fulfillment = item.properties && item.properties._Fulfillment;
                    const fulfillmentLabel = item.handle === 'flux-spark-bundle'
                        ? 'Ordering paused · remove to continue'
                        : fulfillment === 'preorder'
                        ? 'Preorder · timing on product page'
                        : (fulfillment === 'partial' ? 'Partial delivery · see product page' : '');
                    const isDigital = item.requires_shipping === false;
                    return `
        <div class="neuro-cart-item animate-fade-up" style="animation-delay: ${index * 0.05}s">
          
          <div class="nci-image-wrap">
            <img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" class="nci-image" loading="lazy">
          </div>
          
          <div class="nci-content">
            <div class="nci-info">
                <a href="${this.escapeHtml(item.url)}" class="nci-title">${this.escapeHtml(item.product_title)}</a>
                ${item.variant_title ? `<div class="nci-variant">${this.escapeHtml(item.variant_title)}</div>` : ''}
                ${fulfillmentLabel ? `<div class="nci-fulfillment">${this.escapeHtml(fulfillmentLabel)}</div>` : ''}
                <div class="nci-price">${this.formatMoney(item.final_line_price)}</div>
            </div>
            
            <div class="nci-actions">
              ${isDigital ? `<span class="nci-digital">Digital item · qty 1</span>` : `<div class="qty-control">
                <button class="qty-btn" data-qty-minus data-key="${this.escapeHtml(item.key)}" aria-label="Decrease quantity">
                   <svg width="10" height="2" viewBox="0 0 10 2" fill="none"><rect width="10" height="2" rx="1" fill="currentColor"/></svg>
                </button>
                <input type="number" class="qty-input" value="${item.quantity}" readonly data-key="${this.escapeHtml(item.key)}">
                <button class="qty-btn" data-qty-plus data-key="${this.escapeHtml(item.key)}" aria-label="Increase quantity">
                   <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><rect x="4" width="2" height="10" rx="1" fill="currentColor"/><rect y="4" width="10" height="2" rx="1" fill="currentColor"/></svg>
                </button>
              </div>`}

              <!-- Remove Link -->
              <button class="nci-remove" data-remove-item data-key="${this.escapeHtml(item.key)}" aria-label="Remove ${this.escapeHtml(item.product_title)}">
                Remove
              </button>
            </div>
          </div>
          
        </div>
      `;
                }).join('');
            }
        }

        document.dispatchEvent(new CustomEvent('cart:refreshed', { detail: { cart: cart } }));
    }

    updateHeaderCount(count) {
        // Drawer count bubble
        document.querySelectorAll('.cart-count-bubble').forEach((el) => {
            el.textContent = count;
            if (count > 0) el.classList.remove('hidden');
        });

        // "item"/"items" label next to the bubble
        document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
            el.textContent = count === 1 ? 'item' : 'items';
        });

        // Header floating count badge(s)
        document.querySelectorAll('.cart-count-pop[data-cart-count]').forEach((el) => {
            const numberEl = el.querySelector('span[aria-hidden="true"]');
            if (numberEl) numberEl.textContent = count;
            const srEl = el.querySelector('.visually-hidden');
            if (srEl) srEl.textContent = count + (count === 1 ? ' item in cart' : ' items in cart');
            el.style.transform = count > 0 ? 'scale(1)' : 'scale(0)';
            if (count > 0) {
                el.classList.remove('bump');
                requestAnimationFrame(() => el.classList.add('bump'));
            }
        });
    }

    updateHeaderPreview(cart) {
        const totalText = this.formatMoney(cart.total_price);
        document.querySelectorAll('[data-cart-total]').forEach((el) => {
            el.textContent = totalText;
        });
        document.querySelectorAll('[data-cart-preview]').forEach((el) => {
            el.style.display = cart.item_count > 0 ? '' : 'none';
        });
    }

    formatMoney(cents) {
        var amount = (cents / 100).toFixed(2);
        var amountNoDecimals = Math.round(cents / 100).toString();
        var amountWithComma = amount.replace('.', ',');
        return this.moneyFormat
            .replace('{{amount_with_comma_separator}}', amountWithComma)
            .replace('{{amount_no_decimals}}', amountNoDecimals)
            .replace('{{amount}}', amount);
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new CartDrawer();
    // Backward-compatible cart hooks used by legacy sections.
    window.refreshCart = () => {
        if (window.cartDrawer && typeof window.cartDrawer.fetchCart === 'function') {
            window.cartDrawer.fetchCart();
            return;
        }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        window.dispatchEvent(new Event('cart:refresh'));
    };
    window.openCartDrawer = () => {
        if (window.cartDrawer && typeof window.cartDrawer.open === 'function') {
            window.cartDrawer.open();
        }
    };
    window.closeCartDrawer = () => {
        if (window.cartDrawer && typeof window.cartDrawer.close === 'function') {
            window.cartDrawer.close();
        }
    };
});
